import { GridColumnHeader } from '../components/Grid/types';
import type { Attribute, DataSource } from '../types/entities.types';
import { getSearchableFields } from '../utils/searchable.utils';

const gridHeaders: GridColumnHeader[] = [
  {
    name: 'businessMetadata.asset_name',
    label: 'Feature Group Name',
    width: '25%',
    filter: {
      type: 'text',
      remoteSearchFields: ['asset_name'],
    },
    sortable: true,
    remoteSortField: 'asset_name',
  },
  {
    name: 'technicalMetadata.asset_status',
    label: 'Asset Status',
    width: '25%',
    filter: {
      type: 'aggregation',
      aggregationName: 'status_aggregation',
      remoteSearchFields: ['asset_status'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: false,
    remoteSortField: 'asset_status',
  },
  {
    name: 'sourceSpecificMetadata.SageMakerFeatureGroup.aws_account',
    label: 'AWS Account',
    width: '25%',
    filter: {
      type: 'aggregation',
      aggregationName: 'aws_account_aggregation',
      remoteSearchFields: ['aws_account'],
      filterSelectProps: {
        isClearable: true,
      },
    },
    sortable: false,
    remoteSortField: 'aws_account',
  },
];

const attributesMap: Attribute[] = [
  // Basic Information (Column 0)
  {
    name: 'businessMetadata.asset_name',
    label: 'Feature Group Name',
    showInColumn: 0,
    description: 'Name of the SageMaker Feature Group',
    isSearchable: true,
  },
  {
    name: 'technicalMetadata.asset_status',
    label: 'Status',
    showInColumn: 0,
    description: 'Current status of the feature group',
  },
  {
    name: 'technicalMetadata.created_time',
    label: 'Created Time',
    showInColumn: 0,
    description: 'When the feature group was created',
  },
  {
    name: 'sourceSpecificMetadata.SageMakerFeatureGroup.aws_account',
    label: 'AWS Account',
    showInColumn: 0,
    description: 'AWS Account ID where the feature group is hosted',
  },

  // Online Store Configuration (Column 1)
  {
    name: 'sourceSpecificMetadata.SageMakerFeatureGroup.enable_onlinestore',
    label: 'Online Store Enabled',
    type: 'boolean',
    showInColumn: 1,
    description: 'Whether online store is enabled for real-time inference',
  },

  // Technical Configuration (Column 2)
  {
    name: 'sourceSpecificMetadata.SageMakerFeatureGroup.record_identifier',
    label: 'Record Identifier',
    showInColumn: 1,
    description: 'Field used as unique identifier for records',
  },
  {
    name: 'sourceSpecificMetadata.SageMakerFeatureGroup.throughput_mode',
    label: 'Throughput Mode',
    showInColumn: 1,
    description: 'Throughput configuration (OnDemand or Provisioned)',
  },
  {
    name: 'sourceSpecificMetadata.SageMakerFeatureGroup.role_arn',
    label: 'IAM Role ARN',
    showInColumn: 1,
    description: 'AWS IAM role used by the feature group',
  },

  // Features and Parameters (Column 3)
  {
    name: 'sourceSpecificMetadata.SageMakerFeatureGroup.features',
    label: 'Features',
    type: 'table',
    showInColumn: 1,
    description: 'List of features defined in this feature group',
    tableColumns: ['Column Name', 'Data Type', 'Description', 'Tags'],
    tableFieldMapping: {
      'Column Name': 'columnName',
      'Data Type': 'dataType',
      Description: 'description',
      Tags: 'tags',
    },
  },
  {
    name: 'sourceSpecificMetadata.SageMakerFeatureGroup.tags',
    label: 'Parameters',
    type: 'table',
    showInColumn: 2,
    description: 'Parameters associated with the feature group',
    tableColumns: ['Parameter Name', 'Value'],
    tableFieldMapping: {
      'Parameter Name': 'name',
      Value: 'value',
    },
  },
];

const customQueryForListPage = `
  SageMakerFeatureGroup {
    enable_onlinestore
    aws_account
  }
`;

const customQueryForDetailsPage = `
  SageMakerFeatureGroup {
    enable_onlinestore
    record_identifier
    role_arn
    throughput_mode
    aws_account
    tags {
      name
      value
    }
    features {
      columnName
      dataType
      description
      tags {
        name
        value
      }
    }
  }
`;

export const SageMakerFeatureGroupConfig: DataSource = {
  id: '18',
  name: 'SageMakerFeatureGroup',
  category: 'featureGroup',
  label: 'Feature Groups',
  showColumns: false,
  showLineage: true,
  showLineageDashboard: false,
  gridHeaders,
  attributesMap,
  customQueryForListPage,
  customQueryForDetailsPage,
  searchableFields: [...getSearchableFields(attributesMap), 'Feature Names'],
};
